import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import * as _ from "lodash";
import {VisitPlanRouteModel} from "../../../models/visit-plan-route.model";
import {TaskDetailedServiceTimeModel} from "../../../../tasks/models/task-detailed-service-time.model";

require('./visit-plan-routes-panel-list-item.scss');
@Component({
  standalone: false,
  selector: 'app-visit-plan-routes-panel-list-item',
  templateUrl: './visit-plan-routes-panel-list-item.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitPlanRoutesPanelListItemComponent implements OnInit, OnChanges {

  @Input() draft: any;
  @Input() route: VisitPlanRouteModel;
  @Input() settings: any;
  @Input() isSelected: boolean;
  @Input() isVisible: boolean;
  @Input() canSelect?: boolean;
  @Input() canClose?: boolean;
  @Input() canDrilldown?: boolean;
  @Input() itemElementSize?: string;
  @Input() isShowActionMenu: boolean;
  @Input() agentColorMap: { [key: string]: string };
  @Output() onToggle = new EventEmitter<boolean>();
  @Output() onToggleVisible = new EventEmitter<boolean>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() onLockRouteChange = new EventEmitter<{ id: string, locked: boolean }>();
  @Output() onChangeRouteResource = new EventEmitter<{ routeId: string, event: any }>();
  @Output() onEditResourceAllocation = new EventEmitter<{ route: any, event: any }>();
  @Output() onDispatch = new EventEmitter<string>();
  @Output() onReoptimize = new EventEmitter<string>();
  @Output() onPrint = new EventEmitter<string>();
  @Output() onPrintLoadingSheet = new EventEmitter<string>();
  @Output() onDelete = new EventEmitter<string>();
  @Output() onClose = new EventEmitter<any>();
  @Output() onDrillDown = new EventEmitter<{ routeId: string, event: any }>();
  @Output() onRoutePanelContextMenu = new EventEmitter<{ routeId: string, event: any }>();

  public capacity: {
    type: string;
    value: string;
    maxValue: string;
    icon: string;
    percentage: number;
  }
  public routeColor: string;
  public dateRange: { notBefore: string, notAfter: string }
  public isHover: boolean;
  public isCollapsed: boolean = false;
  public isRunning: boolean = false;
  public routeSkills: string[];
  public accountIds: string[];
  public routeTotalIdleTime: number;
  public totalDeliveryUnits: number = 0;
  // Never populated - the "Duration" row it feeds has always been hidden by the *ngIf below.
  public routeDetailedServiceTime: TaskDetailedServiceTimeModel;

  constructor(
    @Inject('planningCommonService') private planningCommonService,
    @Inject('commonsService') private commonsService,
    @Inject('$timeout') private $timeout) {

  }

  ngOnInit() {
    this.setRouteColor();
    this.setDateRange();
    this._setRouteSkills();
    this._setRouteAccountIds();
    this._setRouteTotalIdleTime();
    this._setTotalDeliveryUnits();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.settings && !changes.settings.isFirstChange()) || (changes.route && !changes.route.isFirstChange())) {
      this.setRouteColor();
      this.setDateRange();
      this._setRouteSkills();
      this._setRouteAccountIds();
      this._setRouteTotalIdleTime();
      this._setTotalDeliveryUnits();
      this._setIsRouteProcessRunning();
    }
  }

  onHover(isHover) {
    this.isHover = isHover;
    this.$timeout(0).then(() => {
    });
  }

  onClick($event) {
    this.onSelect.emit($event);
    this.$timeout(0).then(() => {
    });
  }

  onToggleLocked() {
    this.onLockRouteChange.emit({id: this.route.id, locked: !this.route.locked});
  }

  onToggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.$timeout(0).then(() => {
    });
  }

  onDrillDownClick($event) {
    $event.stopPropagation();
    this.onDrillDown.emit($event);
  }

  getTotalIncomeClass(totalIncome?) {
    const minimumRouteIncome = this.route.resource.routeIncomePreferences?.minimumRouteIncome || this.draft?.settings?.incomeSettings?.minRouteIncome;
    const maximumRouteIncome = this.route.resource.routeIncomePreferences?.maximumRouteIncome || this.draft?.settings?.incomeSettings?.maxRouteIncome;
    return (!!minimumRouteIncome && (totalIncome < minimumRouteIncome)) ? 'low' :
      ((!!maximumRouteIncome && (totalIncome > maximumRouteIncome)) ? 'high' : '');
  }

  onContextMenu(event: MouseEvent) {
    event.preventDefault();
    this.onRoutePanelContextMenu.emit({routeId: this.route.id, event})
  }

  private setRouteColor() {
    this.routeColor = !!this.agentColorMap ? this.agentColorMap[this.route.resource.agentId] : this.planningCommonService.getRouteColor(this.route)
    // this.routeColor = this.planningCommonService.getRouteColor(this.route);
  }

  private setDateRange() {
    this.dateRange = {
      notBefore: _.get(_.head(this.route.activities), 'eta'),
      notAfter: _.get(_.last(this.route.activities), 'etd')
    }
  }

  private _setIsRouteProcessRunning() {
    this.isRunning = this.route.actionProcess?.running && (new Date().getTime() < this.route.actionProcess?.expirationDate);
  }

  private _setRouteSkills() {
    const PRIORITY_ORDER = ['Merchandise', 'Order', 'Delivery'] as const;
    const skills = this.route.resource?.allocationSkills || [];
    const prioritySet = new Set(PRIORITY_ORDER);

    const prioritized = PRIORITY_ORDER.filter(skill =>
      skills.includes(skill)
    );

    const rest = skills.filter(skill =>
      !prioritySet.has(skill)
    );

    this.routeSkills = [...prioritized, ...rest];
  }

  private _setRouteAccountIds() {
    let accountIds = [];

    this.route.activities?.forEach((a) => {
      if (a.stopActivities) {
        accountIds = accountIds.concat(_.map(a.stopActivities, 'accountId'));
      } else {
        accountIds.push(a.accountId);
      }
    })

    this.accountIds = _.chain(accountIds)
      .compact()
      .uniq()
      .value()
  }

  private _setRouteTotalIdleTime() {
    let idleTime = 0;

    this.route.activities?.forEach((a) => {
      const activityEta = a.eta;

      if (a.notBefore > activityEta) {
        idleTime += Math.abs(activityEta - a.notBefore);
      }

    })

    this.routeTotalIdleTime = idleTime;
  }

  private _setTotalDeliveryUnits() {
    this.totalDeliveryUnits = 0;
    const stopIdToBundleMap = {};

    this.route?.activities.forEach((a) => {
      if (a.type === 'GROUP' && a.stopIds?.length) {
        a.stopIds.forEach(stopId => {
          stopIdToBundleMap[stopId] = a.id;
        })
      }
    })

    const bundleIdsAlreadyInCount = [];
    this.route?.activityDetails?.forEach((ad) => {
      let needToBeCounted = true;

      if (ad.deliveryUnits) {

        if (stopIdToBundleMap[ad.stopId]) {
          if (_.includes(bundleIdsAlreadyInCount, stopIdToBundleMap[ad.stopId])) {
            needToBeCounted = false;
          } else {
            bundleIdsAlreadyInCount.push(stopIdToBundleMap[ad.stopId]);
          }
        }

        if (needToBeCounted) {
          this.totalDeliveryUnits = this.totalDeliveryUnits + ad.deliveryUnits;
        }
      }

    })
  }

}
